import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import {
  Clock, FolderKanban, Users, FileText, Package,
  ArrowRight, Info, Cog, Receipt, ShoppingCart, Warehouse, Cloud, Inbox,
} from 'lucide-react';
import api from '../../lib/api';
import { KpiCard } from './KpiCard';
import { ChequesEstaSemanaWidget } from '../../components/dashboard/ChequesEstaSemanaWidget';

const ICON_MAP = {
  info: Info,
  cog: Cog,
  receipt: Receipt,
  cart: ShoppingCart,
  warehouse: Warehouse,
  cloud: Cloud,
};

const COLOR_MAP = {
  info: { color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200 hover:border-sky-400' },
  cog: { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200 hover:border-slate-400' },
  receipt: { color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200 hover:border-teal-400' },
  cart: { color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-200 hover:border-indigo-400' },
  warehouse: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200 hover:border-amber-400' },
  cloud: { color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200 hover:border-violet-400' },
};

const ROLE_LABELS = {
  admin: 'Administrador',
  gerencia: 'Gerencia',
  compras: 'Compras',
  contabilidad: 'Contabilidad',
  bodega: 'Bodega',
  operaciones: 'Operaciones',
  consulta: 'Consulta',
};

export function DashboardGeneral({ data }) {
  const [aprobacionesCount, setAprobacionesCount] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/aprobaciones/count');
        if (alive) setAprobacionesCount(res.data?.counts?.total ?? 0);
      } catch {
        if (alive) setAprobacionesCount(0);
      }
    };
    load();
    const i = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(i); };
  }, []);

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="dashboard-general">
      {/* Welcome */}
      <Card className="border-l-4 border-l-blue-600 bg-gradient-to-r from-blue-50/60 to-white">
        <CardContent className="p-5">
          <h2 className="text-xl font-bold text-gray-900" data-testid="welcome-msg">
            Bienvenido, {data.usuario}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Rol: <span className="font-semibold text-blue-700">{ROLE_LABELS[data.rol] || data.rol}</span>
            <span className="mx-2">·</span>
            {data.mes_actual}
          </p>
        </CardContent>
      </Card>

      {/* Global KPIs */}
      <div>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Resumen General</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard
            title="Bandeja Aprobaciones"
            value={aprobacionesCount === null ? '…' : aprobacionesCount}
            icon={Inbox}
            color={aprobacionesCount > 0 ? 'text-amber-700' : 'text-emerald-600'}
            bgColor={aprobacionesCount > 0 ? 'bg-amber-50' : 'bg-emerald-50'}
            subtitle={aprobacionesCount === 0 ? 'Sin pendientes' : 'Pendientes de tu firma'}
            link="/aprobaciones"
            urgent={aprobacionesCount > 0}
          />
          <KpiCard title="OTs Activas" value={data.ots_ejecucion} icon={Clock} color="text-amber-600" bgColor="bg-amber-50" subtitle={`${data.total_ots} totales`} link="/ordenes-trabajo" />
          <KpiCard title="Proyectos" value={data.proyectos_vigentes} icon={FolderKanban} color="text-blue-600" bgColor="bg-blue-50" subtitle={`${data.total_proyectos} totales`} link="/proyectos" />
          <KpiCard title="Clientes" value={data.total_clientes} icon={Users} color="text-emerald-600" bgColor="bg-emerald-50" link="/clientes" />
          <KpiCard title="OCs" value={data.ocs_totales} icon={FileText} color="text-cyan-600" bgColor="bg-cyan-50" link="/compras/seguimiento" />
          <KpiCard title="Recepciones" value={data.total_recepciones} icon={Package} color="text-violet-600" bgColor="bg-violet-50" />
          {/* Iter 286.d — widget compacto de cheques (solo aparece si hay algo) */}
          <ChequesEstaSemanaWidget compact />
        </div>
      </div>

      {/* Accessible modules grid */}
      {data.modulos_acceso?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Accesos Rapidos</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.modulos_acceso.map((mod) => {
              const Icon = ICON_MAP[mod.icon] || Info;
              const colors = COLOR_MAP[mod.icon] || COLOR_MAP.info;
              return (
                <Link key={mod.id} to={mod.link} data-testid={`quick-access-${mod.id}`}>
                  <Card className={`border ${colors.border} transition-all hover:shadow-md cursor-pointer`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${colors.bg}`}>
                        <Icon className={`w-5 h-5 ${colors.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{mod.label}</p>
                        <p className="text-xs text-muted-foreground truncate">{mod.desc}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
